import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogTrigger,
} from "../components/ui/dialog";
import {
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
} from "../components/ui/select";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Plus, Trash, Pencil, ShieldStar, Briefcase } from "@phosphor-icons/react";
import { toast } from "sonner";

const EMPTY = { email: "", name: "", password: "", role: "commercial" };

export default function Users() {
    const { user: me } = useAuth();
    const [users, setUsers] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);

    async function load() {
        const { data } = await api.get("/users");
        setUsers(data);
    }
    useEffect(() => {
        load();
    }, []);

    function openNew() {
        setEditing(null);
        setForm(EMPTY);
        setOpen(true);
    }

    function openEdit(u) {
        setEditing(u);
        setForm({ email: u.email, name: u.name, password: "", role: u.role });
        setOpen(true);
    }

    async function submit(e) {
        e.preventDefault();
        try {
            if (editing) {
                await api.put(`/users/${editing.id}`, {
                    name: form.name,
                    role: form.role,
                    password: form.password || undefined,
                });
                toast.success("Utilisateur mis à jour");
            } else {
                await api.post("/users", form);
                toast.success("Utilisateur créé");
            }
            setOpen(false);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
    }

    async function remove(id) {
        try {
            await api.delete(`/users/${id}`);
            toast.success("Utilisateur supprimé");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    }

    return (
        <Layout>
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
                <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-terracotta mb-2">Équipe</p>
                    <h1 className="font-display text-4xl font-bold text-ink">Utilisateurs</h1>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button
                            onClick={openNew}
                            className="bg-terracotta hover:bg-terracotta-dark text-white"
                            data-testid="new-user-button"
                        >
                            <Plus size={16} className="mr-1.5" /> Nouvel utilisateur
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>
                                {editing ? "Modifier l'utilisateur" : "Nouvel utilisateur"}
                            </DialogTitle>
                        </DialogHeader>
                        <form onSubmit={submit} className="space-y-4">
                            <div>
                                <Label>Nom complet</Label>
                                <Input
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    required
                                    data-testid="user-name-input"
                                />
                            </div>
                            <div>
                                <Label>Email</Label>
                                <Input
                                    type="email"
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    disabled={!!editing}
                                    required
                                    data-testid="user-email-input"
                                />
                            </div>
                            <div>
                                <Label>{editing ? "Nouveau mot de passe (optionnel)" : "Mot de passe"}</Label>
                                <Input
                                    type="password"
                                    value={form.password}
                                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                                    required={!editing}
                                    data-testid="user-password-input"
                                />
                            </div>
                            <div>
                                <Label>Rôle</Label>
                                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                                    <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="admin">Administrateur</SelectItem>
                                        <SelectItem value="commercial">Commercial</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <DialogFooter>
                                <Button
                                    type="submit"
                                    className="bg-terracotta hover:bg-terracotta-dark text-white"
                                    data-testid="submit-user-button"
                                >
                                    {editing ? "Mettre à jour" : "Créer"}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            <div className="bento-card">
                <div className="divide-y divide-sand/60">
                    {users.map((u) => (
                        <div
                            key={u.id}
                            className="flex items-center justify-between py-4"
                            data-testid={`user-row-${u.id}`}
                        >
                            <div className="flex items-center gap-3">
                                <div
                                    className={`w-10 h-10 rounded-md grid place-items-center ${
                                        u.role === "admin"
                                            ? "bg-terracotta/10 text-terracotta"
                                            : "bg-sage/15 text-sage-dark"
                                    }`}
                                >
                                    {u.role === "admin" ? <ShieldStar size={20} weight="duotone" /> : <Briefcase size={20} weight="duotone" />}
                                </div>
                                <div>
                                    <p className="font-medium text-ink">
                                        {u.name}
                                        {u.id === me?.id && (
                                            <span className="text-xs text-ink-soft ml-2">(vous)</span>
                                        )}
                                    </p>
                                    <p className="text-xs text-ink-soft">{u.email}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <Badge
                                    className={
                                        u.role === "admin"
                                            ? "bg-terracotta text-white"
                                            : "bg-sage text-white"
                                    }
                                >
                                    {u.role === "admin" ? "Admin" : "Commercial"}
                                </Badge>
                                <button
                                    onClick={() => openEdit(u)}
                                    className="p-2 text-ink-soft hover:text-ink"
                                    data-testid={`edit-user-${u.id}`}
                                >
                                    <Pencil size={16} />
                                </button>
                                {u.id !== me?.id && (
                                    <AlertDialog>
                                        <AlertDialogTrigger asChild>
                                            <button
                                                className="p-2 text-ink-soft hover:text-destructive"
                                                data-testid={`delete-user-${u.id}`}
                                            >
                                                <Trash size={16} />
                                            </button>
                                        </AlertDialogTrigger>
                                        <AlertDialogContent>
                                            <AlertDialogHeader>
                                                <AlertDialogTitle>Supprimer cet utilisateur ?</AlertDialogTitle>
                                                <AlertDialogDescription>
                                                    {u.name} ({u.email}) sera définitivement retiré du système.
                                                </AlertDialogDescription>
                                            </AlertDialogHeader>
                                            <AlertDialogFooter>
                                                <AlertDialogCancel>Annuler</AlertDialogCancel>
                                                <AlertDialogAction
                                                    onClick={() => remove(u.id)}
                                                    className="bg-destructive text-white"
                                                >
                                                    Supprimer
                                                </AlertDialogAction>
                                            </AlertDialogFooter>
                                        </AlertDialogContent>
                                    </AlertDialog>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </Layout>
    );
}
